import { Descope, AuthProvider } from '@descope/react-sdk/flows';
import { useFlagsFromDualInstances } from '@linktr.ee/feature-flags-mfe/DualStatsigInstances';
import StatsigDualInstanceProvider from '@linktr.ee/feature-flags-mfe/StatsigDualInstanceProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { detectStatsigLocale, getCompoundLocale } from '@shared/i18n';
import {
  getAuthOtpChallengeRequestFailureReasonCode,
  publishAuthOtpChallengeRequestCompletedEvent,
  publishLoginAttemptedEvent,
  type AuthFlow,
  type AuthOtpChannel,
  type AuthOtpRequestTrigger,
} from '@shared/utils/analytics/events';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import { getBrowserId } from '@shared/utils/getBrowserId';
import getPlatformType from '@shared/utils/getPlatformType';
import {
  getMobileStableId,
  isMobileApp,
  isMobileSmsAuthEnabled,
} from '@shared/utils/isMobileApp';
import { prefetchNextHop } from '@shared/utils/prefetchNextHop';
import { reportClientError } from '@shared/utils/reportClientError';
import {
  extractLoginId,
  isConfirmedDisabledError,
  isPotentiallyDisabledUserError,
  redirectIfSuspended,
  redirectToSuspended,
} from '@shared/utils/suspendedUserRedirect';
import { useIsProfileReferral } from '@shared/utils/useIsProfileReferral';
import { ScreenNames } from './screenMapping';
import {
  getArkoseGateVerifyOrBypassAuthFlow,
  routeArkoseGateVerifyOrBypassScreen,
} from '../arkose-gate-verify-or-bypass/routeScreen';
import { isArkoseGateVerifyOrBypassScreen } from '../arkose-gate-verify-or-bypass/screenMapping';
import { routeMfaChallengeScreen } from '../mfa-challenge/routeScreen';
import { isMfaChallengeScreen } from '../mfa-challenge/screenMapping';
import { routePhoneSmsOtpAuthScreen } from '../phone-sms-otp-auth/routeScreen';
import {
  isPhoneSmsOtpAuthScreen,
  PhoneSmsOtpAuthScreenNames,
} from '../phone-sms-otp-auth/screenMapping';
import bannerLoginImage from './images/banner-login-desktop.webp';
import ArkoseGateScreen from './screens/ArkoseGateScreen';
import LoginScreen from './screens/LoginScreen';
import OTPVerificationScreen from './screens/OTPVerificationScreen';
import PasswordLoginScreen from './screens/PasswordLoginScreen';
import PhoneLoginScreen from './screens/PhoneLoginScreen';
import ProtectedEmailConfirmationScreen from './screens/ProtectedEmailConfirmationScreen';
import RegisterScreen from './screens/RegisterScreen';
import UsernameScreen from './screens/UsernameScreen';
import type {
  DescopeFormData,
  DescopeScreenContext,
  DescopeScreenError,
} from './types/descope';
import { useLocalizationModeSync } from './useLocalizationModeSync';
import AddMfaWithSmsOtpFlow, {
  ADD_MFA_WITH_SMS_OTP_FLOW_ID,
} from '../add-mfa-with-sms-otp/App';
import AddMfaWithTotpFlow, {
  ADD_MFA_WITH_TOTP_FLOW_ID,
} from '../add-mfa-with-totp/App';
import ForgotUsernameFlow from '../forgot-username/App';
import { FORGOT_USERNAME_FLOW_ID } from '../forgot-username/screenMapping';
import MobileConsumerPromotionSetEmailFlow, {
  MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID,
} from '../mobile-consumer-promotion-set-email/App';
import RequestPasswordResetFlow from '../request-password-reset/App';
import { REQUEST_PASSWORD_RESET_FLOW_ID } from '../request-password-reset/screenMapping';
import ResetPasswordFlow from '../reset-password/App';
import { RESET_PASSWORD_FLOW_ID } from '../reset-password/screenMapping';
import ResetSmsMfaFlow, {
  ResetTotpMfaFlow,
  RESET_SMS_MFA_FLOW_ID,
  RESET_TOTP_MFA_FLOW_ID,
} from '../reset-sms-mfa/App';
import '@linktr.ee/design-tokens/dist/web/variables.css';
// Arbor ships @font-face for Link Sans Product + Display with relative woff2
// URLs; Vite bundles them same-origin (no CDN/CORS dependency).
import '@linktr.ee/arbor/fonts.css';
import '@shared/style.css';

const DESCOPE_PROJECT_ID =
  import.meta.env.VITE_DESCOPE_PROJECT_ID || 'P32ACVpudk8MNftmpf1LR1pW5k8s';
const DESCOPE_BASE_URL =
  import.meta.env.VITE_DESCOPE_BASE_URL || 'https://ciam.linktr.ee';
const DEFAULT_STATSIG_STAGE = 'production';
const STATSIG_SDK_KEY =
  import.meta.env.VITE_STATSIG_SDK_KEY ||
  'client-J1tag9olmqMG8MoC8GFkRX3e8gX43Gyx3nfJaysnVpi';
const STATSIG_STAGE =
  import.meta.env.VITE_STATSIG_STAGE || DEFAULT_STATSIG_STAGE;
const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';
const UNIVERSAL_LOGIN_FLOW_ID = 'universal-login';
const MOBILE_CONSUMER_PROMOTION_SET_EMAIL_ROUTE =
  '/mobile-consumer-promotion-set-email';
const MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOWS_ROUTE =
  '/flows/mobile-consumer-promotion-set-email';
const ADD_MFA_WITH_SMS_OTP_ROUTE = '/add-mfa-with-sms-otp';
const ADD_MFA_WITH_SMS_OTP_FLOWS_ROUTE = '/flows/add-mfa-with-sms-otp';
const ADD_MFA_WITH_TOTP_ROUTE = '/add-mfa-with-totp';
const ADD_MFA_WITH_TOTP_FLOWS_ROUTE = '/flows/add-mfa-with-totp';
const RESET_SMS_MFA_ROUTE = '/reset-sms-mfa';
const RESET_SMS_MFA_FLOWS_ROUTE = '/flows/reset-sms-mfa';
const RESET_TOTP_MFA_ROUTE = '/reset-totp-mfa';
const RESET_TOTP_MFA_FLOWS_ROUTE = '/flows/reset-totp-mfa';
const REQUEST_PASSWORD_RESET_ROUTE = '/request';
const REQUEST_PASSWORD_RESET_FLOWS_ROUTE = '/flows/request-password-reset';
const FORGOT_USERNAME_ROUTE = '/forgot-username';
const FORGOT_USERNAME_FLOWS_ROUTE = '/flows/forgot-username';
const RESET_PASSWORD_ROUTE_PREFIX = '/reset/';
const RESET_PASSWORD_FLOWS_ROUTE_PREFIX = '/flows/reset-password/';
const RESET_PASSWORD_ROUTE = '/reset';
const RESET_PASSWORD_FLOWS_ROUTE = '/flows/reset-password';
const MOBILE_SMS_AUTH_GATE_ID = 'universal-login-mobile-sms-gate';
const MOBILE_SMS_AUTH_DEBUG_COUNTRIES_GATE_ID =
  'universal-login-mobile-sms-au-and-ua-debug';
const SMS_AUTH_FLOW_FORM_KEY = 'form.smsAuthFlow';
const SMS_AUTH_ALLOWED_COUNTRIES = ['US'] as const;
const SMS_AUTH_DEBUG_ALLOWED_COUNTRIES = ['US', 'AU', 'UA'] as const;
const DESCOPE_EMAIL_OTP_CHALLENGE_FAILURE_TYPE = 'OTPSignUpOrInEmailFailed';
const ARKOSE_LOGIN_GATE_INTERACTION_ID = 'arkose-login-continue';
const ARKOSE_SIGNUP_GATE_INTERACTION_ID = 'arkose-signup-continue';
const ARKOSE_SMS_LOGIN_GATE_INTERACTION_ID = 'arkose-sms-login-continue';
const ARKOSE_SMS_SIGNUP_GATE_INTERACTION_ID = 'arkose-sms-signup-continue';
const PROTECTED_EMAIL_CONFIRMATION_SUBMIT_INTERACTION_ID = 'L4SwjB0Qpa';
const AUTH_IDENTIFIER_VALIDATION_PURPOSE_KEY = 'form.validationPurpose';
const AUTH_IDENTIFIER_CONTINUE_VALIDATION_PURPOSE = 'identifier_continue';
const EMAIL_OTP_FALLBACK_VALIDATION_PURPOSE = 'email_otp_fallback';
const PROTECTED_EMAIL_CONFIRMATION_SCREEN_NAMES = [
  ScreenNames.CONFIRM_EMAIL,
  'ProtectedEmailConfirmationScreen',
  'Protected Email Confirmation Screen',
  'Protected Username Email Confirmation',
  'Protected username email confirmation',
] as const;
const LOGIN_OTP_SCREEN_NAMES = [
  ScreenNames.VERIFY_OTP,
  'Verify email OTP in Login',
] as const;
const DESCOPE_REGISTER_HASH_QUERY_PARAM_WHITELIST = [
  'freeTrial',
  'freeTrialEntryPoint',
  'frequency',
  'tier',
  'couponId',
  'addons',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeSdkNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type UniversalLoginFlowState = DescopeScreenContext & {
  screenName?: string;
  error?: DescopeScreenError;
  next?: DescopeNext;
  lastAuth?: { loginId?: string };
};

interface DescopeBridge {
  abortFlow?: (reason?: string) => void;
}

const getDescopeBridge = (): DescopeBridge | undefined =>
  (window as Window & { descopeBridge?: DescopeBridge }).descopeBridge;

const withValidationPurpose = (
  payload: Record<string, unknown>,
  validationPurpose: string
): Record<string, unknown> => ({
  ...payload,
  [AUTH_IDENTIFIER_VALIDATION_PURPOSE_KEY]: validationPurpose,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const toDescopeScreenError = (error: unknown): DescopeScreenError =>
  isRecord(error) ? error : { detail: error };

const getRecordField = (
  record: Record<string, unknown>,
  field: string
): Record<string, unknown> | undefined => {
  const value = record[field];
  return isRecord(value) ? value : undefined;
};

const getStringField = (
  record: Record<string, unknown>,
  field: string
): string | undefined => {
  const value = record[field];
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
};

const getBooleanField = (
  record: Record<string, unknown>,
  field: string
): boolean | undefined => {
  const value = record[field];
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === 'true') {
      return true;
    }
    if (normalizedValue === 'false') {
      return false;
    }
  }

  return undefined;
};

const getNumberField = (
  record: Record<string, unknown>,
  field: string
): number | undefined => {
  const value = record[field];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

const getProtectedMaskedEmailFromContext = (
  context: unknown
): string | undefined => {
  if (!isRecord(context)) {
    return undefined;
  }

  const connectors = getRecordField(context, 'connectors');
  const httpResult = connectors
    ? getRecordField(connectors, 'httpResult')
    : undefined;

  return (
    (httpResult ? getStringField(httpResult, 'maskedEmail') : undefined) ||
    getStringField(context, 'maskedEmail')
  );
};

const getProtectedEmailCooldownFromContext = (
  context: unknown
): { rateLimited?: boolean; retryAfterSeconds?: number } => {
  if (!isRecord(context)) {
    return {};
  }

  const connectors = getRecordField(context, 'connectors');
  const protectedEmailVerificationResult = connectors
    ? getRecordField(connectors, 'protectedEmailVerificationResult')
    : undefined;
  const source = protectedEmailVerificationResult || context;

  return {
    rateLimited:
      getBooleanField(source, 'rateLimited') ??
      getBooleanField(source, 'isRateLimited'),
    retryAfterSeconds:
      getNumberField(source, 'retryAfterSeconds') ??
      getNumberField(source, 'retryAfter') ??
      getNumberField(source, 'retryAfterSecs'),
  };
};

const isEmailOtpChallengeFailure = (
  error: unknown
): error is DescopeScreenError & {
  type: typeof DESCOPE_EMAIL_OTP_CHALLENGE_FAILURE_TYPE;
} => isRecord(error) && error.type === DESCOPE_EMAIL_OTP_CHALLENGE_FAILURE_TYPE;

const isProtectedEmailConfirmationScreen = (screenName?: string): boolean =>
  Boolean(
    screenName &&
      PROTECTED_EMAIL_CONFIRMATION_SCREEN_NAMES.includes(
        screenName as (typeof PROTECTED_EMAIL_CONFIRMATION_SCREEN_NAMES)[number]
      )
  );

const isLoginOtpVerificationScreen = (screenName?: string): boolean =>
  Boolean(
    screenName &&
      LOGIN_OTP_SCREEN_NAMES.includes(
        screenName as (typeof LOGIN_OTP_SCREEN_NAMES)[number]
      )
  );

const isSignupOtpVerificationScreen = (screenName?: string): boolean =>
  screenName === ScreenNames.VERIFY_OTP_SIGNUP;

const isEmailOtpVerificationScreen = (screenName?: string): boolean =>
  isLoginOtpVerificationScreen(screenName) ||
  isSignupOtpVerificationScreen(screenName);

const getPreVerificationOtpFailureAuthFlow = (
  screenName: string,
  context?: Record<string, unknown>,
  form?: Record<string, unknown>
): AuthFlow | undefined => {
  if (isArkoseGateVerifyOrBypassScreen(screenName)) {
    return getArkoseGateVerifyOrBypassAuthFlow(context, form);
  }

  if (
    screenName === ScreenNames.SIGNUP_PAGE ||
    screenName === ScreenNames.ARKOSE_SIGNUP_GATE ||
    screenName === ScreenNames.ARKOSE_SMS_SIGNUP_GATE
  ) {
    return 'signup';
  }

  if (
    screenName === ScreenNames.LOGIN_PAGE ||
    screenName === ScreenNames.PASSWORD_OTP_LOGIN ||
    screenName === ScreenNames.ARKOSE_LOGIN_GATE ||
    screenName === ScreenNames.ARKOSE_SMS_LOGIN_GATE
  ) {
    return 'login';
  }

  return undefined;
};

const getInitialOtpFailureSignature = (
  authFlow: AuthFlow,
  otpChannel: AuthOtpChannel,
  screenName: string,
  error: unknown
): string => {
  const errorType = isRecord(error) ? error.type : undefined;
  const errorText = isRecord(error) ? error.text : undefined;

  return [
    authFlow,
    otpChannel,
    screenName,
    typeof errorType === 'string' ? errorType : '',
    typeof errorText === 'string' ? errorText : '',
  ].join('|');
};

const omitSmsAuthFlow = (
  value: Record<string, unknown>
): Record<string, unknown> => {
  const nextValue = { ...value };
  delete nextValue[SMS_AUTH_FLOW_FORM_KEY];
  delete nextValue.smsAuthFlow;
  return nextValue;
};

const SIGNUP_MODE_SCREENS: string[] = [
  ScreenNames.SIGNUP_PAGE,
  ScreenNames.ARKOSE_SIGNUP_GATE,
  ScreenNames.ARKOSE_SMS_SIGNUP_GATE,
  ScreenNames.VERIFY_OTP_SIGNUP,
  ScreenNames.CHOOSE_USERNAME_EMAIL_OTP,
  ScreenNames.SOCIAL_SIGNUP_USERNAME,
];

// Monolith path the signup flow lands on after a successful auth.
// Prefetching it collapses next-hop TTFB to edge-POP RTT (MONEY-792).
//
// Only the signup landing is prefetched. The login landing (`/admin`) is
// auth-gated by the monolith firewall, so a prefetch issued from the
// (logged-out) login screen is denied and 302'd to `/login`. That denied
// request overwrites the firewall's saved `target_path` with `/admin`,
// discarding the user's original deep link (e.g. `/admin/settings`) and
// dropping them on the bare dashboard post-login (APP-1631). It also yields
// no perf benefit, since the redirect (not the admin HTML) is what gets
// cached. Do NOT add an `/admin` prefetch here.
const SIGNUP_REDIRECT_PATH = '/register/get-started';

const normaliseRoute = (route: string): string => {
  const [routeWithoutQuery = ''] = route.split('?');
  return routeWithoutQuery.replace(/^#/, '').replace(/\/$/, '') || '/';
};

const isMobileConsumerPromotionSetEmailRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === MOBILE_CONSUMER_PROMOTION_SET_EMAIL_ROUTE ||
    hashRoute === MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOWS_ROUTE ||
    pathRoute.endsWith(MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOWS_ROUTE) ||
    pathRoute.endsWith(MOBILE_CONSUMER_PROMOTION_SET_EMAIL_ROUTE)
  );
};

const isAddMfaWithSmsOtpRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === ADD_MFA_WITH_SMS_OTP_ROUTE ||
    hashRoute === ADD_MFA_WITH_SMS_OTP_FLOWS_ROUTE ||
    pathRoute.endsWith(ADD_MFA_WITH_SMS_OTP_ROUTE) ||
    pathRoute.endsWith(ADD_MFA_WITH_SMS_OTP_FLOWS_ROUTE)
  );
};

const isAddMfaWithTotpRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === ADD_MFA_WITH_TOTP_ROUTE ||
    hashRoute === ADD_MFA_WITH_TOTP_FLOWS_ROUTE ||
    pathRoute.endsWith(ADD_MFA_WITH_TOTP_ROUTE) ||
    pathRoute.endsWith(ADD_MFA_WITH_TOTP_FLOWS_ROUTE)
  );
};

const isResetSmsMfaRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === RESET_SMS_MFA_ROUTE ||
    hashRoute === RESET_SMS_MFA_FLOWS_ROUTE ||
    pathRoute.endsWith(RESET_SMS_MFA_ROUTE) ||
    pathRoute.endsWith(RESET_SMS_MFA_FLOWS_ROUTE)
  );
};

const isResetTotpMfaRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === RESET_TOTP_MFA_ROUTE ||
    hashRoute === RESET_TOTP_MFA_FLOWS_ROUTE ||
    pathRoute.endsWith(RESET_TOTP_MFA_ROUTE) ||
    pathRoute.endsWith(RESET_TOTP_MFA_FLOWS_ROUTE)
  );
};

const isRequestPasswordResetRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === REQUEST_PASSWORD_RESET_ROUTE ||
    hashRoute === REQUEST_PASSWORD_RESET_FLOWS_ROUTE ||
    pathRoute.endsWith(REQUEST_PASSWORD_RESET_ROUTE) ||
    pathRoute.endsWith(REQUEST_PASSWORD_RESET_FLOWS_ROUTE)
  );
};

const isForgotUsernameRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === FORGOT_USERNAME_ROUTE ||
    hashRoute === FORGOT_USERNAME_FLOWS_ROUTE ||
    pathRoute.endsWith(FORGOT_USERNAME_ROUTE) ||
    pathRoute.endsWith(FORGOT_USERNAME_FLOWS_ROUTE)
  );
};

const getRouteToken = (route: string, prefix: string): string | undefined => {
  const routeIndex = route.lastIndexOf(prefix);

  if (routeIndex === -1) {
    return undefined;
  }

  const token = route.slice(routeIndex + prefix.length).split('/')[0];

  if (!token) {
    return undefined;
  }

  try {
    return decodeURIComponent(token);
  } catch {
    return token;
  }
};

const getResetPasswordTokenFromRoute = (): string | undefined => {
  const routes = [
    normaliseRoute(window.location.hash),
    normaliseRoute(window.location.pathname),
  ];

  for (const route of routes) {
    const token =
      getRouteToken(route, RESET_PASSWORD_ROUTE_PREFIX) ??
      getRouteToken(route, RESET_PASSWORD_FLOWS_ROUTE_PREFIX);

    if (token) {
      return token;
    }
  }

  return undefined;
};

const isResetPasswordRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);
  const routes = [hashRoute, pathRoute];

  return routes.some(
    route =>
      route === RESET_PASSWORD_ROUTE ||
      route === RESET_PASSWORD_FLOWS_ROUTE ||
      route.includes(RESET_PASSWORD_ROUTE_PREFIX) ||
      route.includes(RESET_PASSWORD_FLOWS_ROUTE_PREFIX)
  );
};

const getInitialFlowId = (): string =>
  isResetPasswordRoute()
    ? RESET_PASSWORD_FLOW_ID
    : isForgotUsernameRoute()
      ? FORGOT_USERNAME_FLOW_ID
      : isRequestPasswordResetRoute()
        ? REQUEST_PASSWORD_RESET_FLOW_ID
        : isResetTotpMfaRoute()
          ? RESET_TOTP_MFA_FLOW_ID
          : isResetSmsMfaRoute()
            ? RESET_SMS_MFA_FLOW_ID
            : isAddMfaWithTotpRoute()
              ? ADD_MFA_WITH_TOTP_FLOW_ID
              : isAddMfaWithSmsOtpRoute()
                ? ADD_MFA_WITH_SMS_OTP_FLOW_ID
                : isMobileConsumerPromotionSetEmailRoute()
                  ? MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID
                  : UNIVERSAL_LOGIN_FLOW_ID;

/**
 * Promotes the paid-media signup params from the hash into the real query
 * string so they survive the Descope auth hop, preserve UTM attribution, and
 * continue into onboarding.
 */
const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);

  // Native flows need the hash route and params after Descope redirects back.
  if (isMobileApp()) {
    return url.toString();
  }

  const hashRoute = normaliseRoute(url.hash);

  if (hashRoute === '/register') {
    const hashQueryString = url.hash.split('?')[1];

    if (hashQueryString) {
      const hashSearchParams = new URLSearchParams(hashQueryString);

      DESCOPE_REGISTER_HASH_QUERY_PARAM_WHITELIST.forEach(paramName => {
        const paramValue = hashSearchParams.get(paramName);

        if (paramValue !== null && !url.searchParams.has(paramName)) {
          url.searchParams.set(paramName, paramValue);
        }
      });
    }
  }

  url.hash = '';
  return url.toString();
};

/**
 * Collects whitelisted auth params from the current URL so the login fallback
 * can preserve paid-media and UTM attribution across the Descope hop.
 */
const getWhitelistedDescopeQueryParams = (): URLSearchParams => {
  const whitelistedQueryParams = new URLSearchParams();
  const searchParams = new URLSearchParams(window.location.search);
  const hashQueryString =
    normaliseRoute(window.location.hash) === '/register'
      ? window.location.hash.split('?')[1]
      : undefined;
  const hashParams = hashQueryString
    ? new URLSearchParams(hashQueryString)
    : undefined;

  DESCOPE_REGISTER_HASH_QUERY_PARAM_WHITELIST.forEach(paramName => {
    const paramValue =
      searchParams.get(paramName) ?? hashParams?.get(paramName);

    if (paramValue !== undefined && paramValue !== null) {
      whitelistedQueryParams.set(paramName, paramValue);
    }
  });

  return whitelistedQueryParams;
};

const HostedDescopeFlow: React.FC<{ flowId: string }> = ({ flowId }) => {
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);

  return (
    <Descope
      flowId={flowId}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId({})}
    />
  );
};

const UniversalLoginFlow: React.FC = () => {
  const [state, setState] = useState<UniversalLoginFlowState>({ error: {} });
  const [form, setForm] = useState<DescopeFormData>({});
  const [descopeFlowResetKey, setDescopeFlowResetKey] = useState(0);
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [whitelistedRedirectQueryParams] = useState(
    getWhitelistedDescopeQueryParams
  );
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const skipNextInitialOtpChallengeEventsRef = useRef(false);
  const hasSeenOtpVerificationScreenRef = useRef(false);
  const lastInitialOtpChallengeFailureSignatureRef = useRef<string | undefined>(
    undefined
  );
  // TRU-3217: onScreenUpdate re-fires whenever React re-renders the Descope
  // shell after a screen-update event, and the disabled-error detection
  // (OTPSignUpOrInEmailFailed) is intentionally broad — it fires for every
  // OTP/email login failure, not just real suspensions. Without a guard we'd
  // re-hit the monolith on every re-render of the same error state.
  //
  // Tracks loginIds we've *already asked the monolith about* for the lifetime
  // of this component (not just in-flight requests). Once a check has been
  // dispatched for a given loginId we don't ask again — even if the call
  // resolved with suspended:false (the common case) and the same error context
  // fires again on the next re-render. The user can still trigger a fresh
  // check by typing a different identifier (different key) or refreshing the
  // page (component remount → fresh ref).
  const suspensionCheckedRef = useRef<Set<string>>(new Set());
  const [prefilledUsername] = useState<string | null>(() => {
    const hash = window.location.hash;
    // Expected Hash format: #/register?username={username}
    const queryString = hash.split('?')[1];
    if (queryString) {
      const searchParams = new URLSearchParams(queryString);
      const usernameParam = searchParams.get('username');
      if (usernameParam) {
        return usernameParam;
      }
    }
    return null;
  });
  const isProfileReferral = useIsProfileReferral();
  const [mobileSmsEligibility] = useState(() => ({
    isMobileAppSession: isMobileApp(),
    isSmsAuthQueryEnabled: isMobileSmsAuthEnabled(),
  }));
  const { isMobileAppSession, isSmsAuthQueryEnabled } = mobileSmsEligibility;
  const { getFlagFromWarehouseNativeWithExposure, initialised } =
    useFlagsFromDualInstances();
  const showSmsAuth =
    isSmsAuthQueryEnabled &&
    initialised &&
    Boolean(getFlagFromWarehouseNativeWithExposure(MOBILE_SMS_AUTH_GATE_ID));
  const isSmsPhoneScreen =
    state?.screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN;
  const showDebugSmsCountries =
    isSmsPhoneScreen &&
    initialised &&
    Boolean(
      getFlagFromWarehouseNativeWithExposure(
        MOBILE_SMS_AUTH_DEBUG_COUNTRIES_GATE_ID
      )
    );
  const smsAuthAllowedCountries = showDebugSmsCountries
    ? SMS_AUTH_DEBUG_ALLOWED_COUNTRIES
    : SMS_AUTH_ALLOWED_COUNTRIES;
  const platformType = getPlatformType(isProfileReferral, isMobileAppSession);
  const emailOtpLoginAttemptPublishedRef = useRef(false);
  const [smsPhone, setSmsPhone] = useState<string | undefined>();
  const formWithoutSmsAuthFlow = omitSmsAuthFlow(form);
  const smsAuthForm = {
    ...(state?.form ?? {}),
    ...formWithoutSmsAuthFlow,
    ...(smsPhone ? { phone: smsPhone } : {}),
    [SMS_AUTH_FLOW_FORM_KEY]: 'login',
  };
  const smsScreenContext = {
    ...state,
    form: smsAuthForm,
  };
  const arkoseGateForm = {
    ...(isRecord(state?.form) ? state.form : {}),
    ...formWithoutSmsAuthFlow,
  };
  const isLoginOtpVerification = isLoginOtpVerificationScreen(
    state?.screenName
  );
  const isSignupOtpVerification = isSignupOtpVerificationScreen(
    state?.screenName
  );

  const resetEmailOtpLoginAttempt = useCallback(() => {
    emailOtpLoginAttemptPublishedRef.current = false;
  }, []);

  const markEmailOtpLoginAttemptPublished = useCallback(() => {
    emailOtpLoginAttemptPublishedRef.current = true;
  }, []);

  const publishEmailOtpLoginAttemptOnce = useCallback(() => {
    if (emailOtpLoginAttemptPublishedRef.current) {
      return;
    }

    emailOtpLoginAttemptPublishedRef.current = true;
    publishLoginAttemptedEvent('Email OTP', platformType);
  }, [platformType]);

  const markExplicitOtpChallengeRequested = useCallback(() => {
    skipNextInitialOtpChallengeEventsRef.current = true;
  }, []);

  const resetExplicitOtpChallengeRequest = useCallback(() => {
    skipNextInitialOtpChallengeEventsRef.current = false;
  }, []);

  const resetDescopeError = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      error: {},
    }));
  }, []);

  const updateForm = useCallback((nextForm: DescopeFormData) => {
    setForm(nextForm);
  }, []);

  const resetInitialOtpChallengeFailureReporting = useCallback(() => {
    lastInitialOtpChallengeFailureSignatureRef.current = undefined;
  }, []);

  const resetMobileSmsLoginFlow = useCallback((): boolean => {
    if (!isMobileAppSession) {
      return false;
    }

    const bridge = getDescopeBridge();
    if (!bridge?.abortFlow) {
      return false;
    }

    setSmsPhone(undefined);
    setForm({});
    setState({ error: {} });
    setDescopeFlowResetKey(resetKey => resetKey + 1);
    hasSeenOtpVerificationScreenRef.current = false;
    resetInitialOtpChallengeFailureReporting();
    resetExplicitOtpChallengeRequest();
    resetEmailOtpLoginAttempt();
    bridge.abortFlow();

    return true;
  }, [
    isMobileAppSession,
    resetEmailOtpLoginAttempt,
    resetExplicitOtpChallengeRequest,
    resetInitialOtpChallengeFailureReporting,
  ]);

  const consumeInitialOtpChallengeRequestTrigger = useCallback(():
    | AuthOtpRequestTrigger
    | undefined => {
    if (skipNextInitialOtpChallengeEventsRef.current) {
      skipNextInitialOtpChallengeEventsRef.current = false;
      return undefined;
    }

    return 'initial_route';
  }, []);

  useEffect(() => {
    const screenName = state?.screenName;
    if (!screenName) {
      return;
    }
    if (SIGNUP_MODE_SCREENS.includes(screenName)) {
      prefetchNextHop(SIGNUP_REDIRECT_PATH);
    }
  }, [state?.screenName]);

  return (
    <Descope
      key={descopeFlowResetKey}
      flowId={UNIVERSAL_LOGIN_FLOW_ID}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(formWithoutSmsAuthFlow)}
      onScreenUpdate={(
        screenName: string,
        context: UniversalLoginFlowState,
        next: DescopeSdkNext
      ) => {
        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));

        // Initial-route OTP challenge failures are observable here before the
        // verification screen mounts: Descope returns the structured
        // OTPSignUpOrInEmailFailed type in context.error. That identifies the
        // email OTP challenge path without using the raw recipient, OTP code,
        // or provider-specific error copy. onError does not carry the target
        // screen/channel context consistently enough for this OTP event.
        if (
          !hasSeenOtpVerificationScreenRef.current &&
          isEmailOtpChallengeFailure(context?.error)
        ) {
          const authFlow = getPreVerificationOtpFailureAuthFlow(
            screenName,
            context,
            form
          );
          const otpChannel: AuthOtpChannel = 'email';

          if (!skipNextInitialOtpChallengeEventsRef.current && authFlow) {
            const failureSignature = getInitialOtpFailureSignature(
              authFlow,
              otpChannel,
              screenName,
              context.error
            );

            if (
              failureSignature !==
              lastInitialOtpChallengeFailureSignatureRef.current
            ) {
              lastInitialOtpChallengeFailureSignatureRef.current =
                failureSignature;
              publishAuthOtpChallengeRequestCompletedEvent(
                authFlow,
                'initial_route',
                'failed',
                getAuthOtpChallengeRequestFailureReasonCode(context.error),
                otpChannel
              );
            }
          }
        }

        if (
          isEmailOtpVerificationScreen(screenName) ||
          screenName === PhoneSmsOtpAuthScreenNames.VERIFY_SMS_OTP
        ) {
          hasSeenOtpVerificationScreenRef.current = true;
          resetInitialOtpChallengeFailureReporting();
        } else if (
          screenName === ScreenNames.LOGIN_PAGE ||
          screenName === ScreenNames.SIGNUP_PAGE ||
          screenName === ScreenNames.ARKOSE_LOGIN_GATE ||
          screenName === ScreenNames.ARKOSE_SIGNUP_GATE ||
          screenName === ScreenNames.ARKOSE_SMS_LOGIN_GATE ||
          screenName === ScreenNames.ARKOSE_SMS_SIGNUP_GATE ||
          isArkoseGateVerifyOrBypassScreen(screenName) ||
          screenName === ScreenNames.PASSWORD_OTP_LOGIN ||
          screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN ||
          screenName === ScreenNames.CONFIRM_EMAIL
        ) {
          hasSeenOtpVerificationScreenRef.current = false;
        }

        if (isPotentiallyDisabledUserError(context?.error)) {
          const loginId = extractLoginId(context, form);
          if (loginId && !suspensionCheckedRef.current.has(loginId)) {
            suspensionCheckedRef.current.add(loginId);
            void redirectIfSuspended(loginId, LINKTREE_BASE_URL);
          }
        }

        setState(prevState => ({
          ...prevState,
          ...context,
          next: nextWithClientSessionId,
          screenName,
          error: context?.error || {},
        }));

        const customScreenNames: readonly string[] = [
          ScreenNames.LOGIN_PAGE,
          ScreenNames.SIGNUP_PAGE,
          ScreenNames.PASSWORD_OTP_LOGIN,
          ...LOGIN_OTP_SCREEN_NAMES,
          ScreenNames.VERIFY_OTP_SIGNUP,
          ScreenNames.CHOOSE_USERNAME_EMAIL_OTP,
          ScreenNames.SOCIAL_SIGNUP_USERNAME,
          ScreenNames.ARKOSE_LOGIN_GATE,
          ScreenNames.ARKOSE_SIGNUP_GATE,
          ScreenNames.ARKOSE_SMS_LOGIN_GATE,
          ScreenNames.ARKOSE_SMS_SIGNUP_GATE,
          ...PROTECTED_EMAIL_CONFIRMATION_SCREEN_NAMES,
        ];
        return (
          customScreenNames.includes(screenName) ||
          isArkoseGateVerifyOrBypassScreen(screenName) ||
          isPhoneSmsOtpAuthScreen(screenName) ||
          isMfaChallengeScreen(screenName)
        );
      }}
      onSuccess={() => {
        setState(prevState => ({ ...prevState }));
        // The redirect to monolith should automatically happen, this is a fallback for when it doesn't.
        if (!isMobileAppSession) {
          if (whitelistedRedirectQueryParams.toString()) {
            const loginUrl = new URL('/login', LINKTREE_BASE_URL);
            loginUrl.searchParams.set('redirect_path', '/admin');
            whitelistedRedirectQueryParams.forEach((value, key) => {
              loginUrl.searchParams.append(key, value);
            });
            window.location.href = loginUrl.toString();
          } else {
            window.location.href = `${LINKTREE_BASE_URL}/login`;
          }
        }
      }}
      onError={(e: unknown) => {
        const errorRecord = isRecord(e) ? e : undefined;
        reportClientError('Descope Flow Error:', e);
        reportClientError('Error details:', {
          type: errorRecord?.type,
          detail: errorRecord?.detail,
          target: errorRecord?.target,
          currentTarget: errorRecord?.currentTarget,
        });

        // TRU-3217: OAuth disabled-user errors surface here (not onScreenUpdate)
        // — Descope bounces back to the Login page and clears context.error, so
        // the screen-update path can't see them. The error string carries
        // E023001 verbatim, so the detection here is unambiguous. Prefer the
        // monolith disambiguation when we have a loginId (e.g. lastAuth got
        // populated from a prior identification step), otherwise redirect
        // directly — OAuth visitors never typed an identifier, so the monolith
        // would have nothing to look up.
        const errorDetail = errorRecord?.detail ?? e;
        if (
          isConfirmedDisabledError(
            isRecord(errorDetail) ? errorDetail : undefined
          )
        ) {
          const loginId = extractLoginId(state, form);
          if (loginId && !suspensionCheckedRef.current.has(loginId)) {
            suspensionCheckedRef.current.add(loginId);
            void redirectIfSuspended(loginId, LINKTREE_BASE_URL);
          } else if (!loginId) {
            redirectToSuspended(LINKTREE_BASE_URL);
          }
        }

        setState(prevState => ({
          ...prevState,
          error: toDescopeScreenError(errorDetail),
        }));
      }}
    >
      {isProtectedEmailConfirmationScreen(state?.screenName) && (
        <ProtectedEmailConfirmationScreen
          maskedEmail={getProtectedMaskedEmailFromContext(state)}
          {...getProtectedEmailCooldownFromContext(state)}
          onClickConfirm={async (email: string) => {
            if (state.next) {
              resetDescopeError();
              const nextForm = {
                ...(isRecord(state?.form) ? state.form : {}),
                ...(isRecord(form) ? form : {}),
                email,
              };
              setForm(nextForm);
              await state.next(
                PROTECTED_EMAIL_CONFIRMATION_SUBMIT_INTERACTION_ID,
                nextForm
              );
            }
          }}
          onClickUseDifferentLogin={() => {
            window.location.href = `${LINKTREE_BASE_URL}/login`;
          }}
          errorText={state?.error?.text}
        />
      )}
      {state?.screenName === ScreenNames.LOGIN_PAGE && (
        <LoginScreen
          onFormUpdate={updateForm}
          onClickContinue={async () => {
            if (state.next) {
              resetInitialOtpChallengeFailureReporting();
              await state.next(
                'email-otp-login',
                withValidationPurpose(
                  form,
                  AUTH_IDENTIFIER_CONTINUE_VALIDATION_PURPOSE
                )
              );
            }
          }}
          onClickSMS={async () => {
            if (state.next) {
              const nextForm = {
                ...formWithoutSmsAuthFlow,
                [SMS_AUTH_FLOW_FORM_KEY]: 'login',
              };

              setForm(formWithoutSmsAuthFlow);
              await state.next('bD1FMXdZPo', nextForm);
            }
          }}
          onClickGoogle={async () => {
            if (state.next) {
              await state.next('w37cm8AmxC', { provider: 'google' });
            }
          }}
          onClickApple={async () => {
            if (state.next) {
              await state.next('cJiO2sA0HU', { provider: 'apple' });
            }
          }}
          onClickSignUp={async () => {
            if (state.next) {
              await state.next('ACXl0WypGW', form);
            }
          }}
          onIdentifierSubmitted={resetEmailOtpLoginAttempt}
          onEmailOtpAttempted={markEmailOtpLoginAttemptPublished}
          showSmsLogin={showSmsAuth}
          errorText={state?.error?.text}
          context={state}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
        />
      )}
      {state?.screenName === ScreenNames.ARKOSE_LOGIN_GATE && (
        <ArkoseGateScreen
          authFlow="login"
          backHref={`${LINKTREE_BASE_URL}/login`}
          onContinue={async () => {
            if (state.next) {
              await state.next(
                ARKOSE_LOGIN_GATE_INTERACTION_ID,
                arkoseGateForm
              );
            }
          }}
          errorText={state?.error?.text}
        />
      )}
      {state?.screenName === ScreenNames.ARKOSE_SIGNUP_GATE && (
        <ArkoseGateScreen
          authFlow="signup"
          backHref={`${LINKTREE_BASE_URL}${SIGNUP_REDIRECT_PATH}`}
          onContinue={async () => {
            if (state.next) {
              await state.next(
                ARKOSE_SIGNUP_GATE_INTERACTION_ID,
                arkoseGateForm
              );
            }
          }}
          errorText={state?.error?.text}
        />
      )}
      {state?.screenName === ScreenNames.ARKOSE_SMS_LOGIN_GATE && (
        <ArkoseGateScreen
          authFlow="login"
          backHref={`${LINKTREE_BASE_URL}/login`}
          onContinue={async () => {
            if (state.next) {
              await state.next(
                ARKOSE_SMS_LOGIN_GATE_INTERACTION_ID,
                smsAuthForm
              );
            }
          }}
          errorText={state?.error?.text}
        />
      )}
      {state?.screenName === ScreenNames.ARKOSE_SMS_SIGNUP_GATE && (
        <ArkoseGateScreen
          authFlow="signup"
          backHref={`${LINKTREE_BASE_URL}${SIGNUP_REDIRECT_PATH}`}
          onContinue={async () => {
            if (state.next) {
              await state.next(
                ARKOSE_SMS_SIGNUP_GATE_INTERACTION_ID,
                arkoseGateForm
              );
            }
          }}
          errorText={state?.error?.text}
        />
      )}
      {routeArkoseGateVerifyOrBypassScreen({
        screenName: state?.screenName,
        state,
        form: arkoseGateForm,
        smsAuthForm,
        loginBackHref: `${LINKTREE_BASE_URL}/login`,
        signupBackHref: `${LINKTREE_BASE_URL}${SIGNUP_REDIRECT_PATH}`,
        ArkoseGateScreen,
      })}
      {state?.screenName === ScreenNames.SIGNUP_PAGE && (
        <RegisterScreen
          onChange={(data: DescopeFormData) =>
            setForm(prev => ({ ...prev, ...data }))
          }
          onClickContinue={async () => {
            if (state.next) {
              resetInitialOtpChallengeFailureReporting();
              await state.next('c6ffx0tFb9', form);
            }
          }}
          onClickGoogle={async () => {
            if (state.next) {
              await state.next('e6emQLX4gJ', { provider: 'google', ...form });
            }
          }}
          onClickApple={async () => {
            if (state.next) {
              await state.next('ThET2KRZVy', { provider: 'apple', ...form });
            }
          }}
          onClickLogin={async () => {
            if (state.next) {
              await state.next('B5gkpEXlCm', form);
            }
          }}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
          errorText={state?.error?.text}
          context={state}
          prefilledUsername={prefilledUsername}
        />
      )}
      {state?.screenName === ScreenNames.PASSWORD_OTP_LOGIN && (
        <PasswordLoginScreen
          onFormUpdate={updateForm}
          onClickPasswordLogin={async () => {
            if (state.next) {
              await state.next('A0n-0HflX3', form);
            }
          }}
          onClickEmailOTP={async () => {
            if (state.next) {
              await state.next(
                'ytyEctTvDe',
                withValidationPurpose(
                  form,
                  EMAIL_OTP_FALLBACK_VALIDATION_PURPOSE
                )
              );
            }
          }}
          onEmailOtpChallengeRequested={markExplicitOtpChallengeRequested}
          onEmailOtpChallengeRequestFailed={resetExplicitOtpChallengeRequest}
          onClickEditEmailUsername={async () => {
            if (state.next) {
              await state.next('xX0SpghuGd', form);
            }
          }}
          onClickSignUp={async () => {
            if (state.next) {
              await state.next('CCf_I2IX05', form);
            }
          }}
          onEmailOtpAttempted={markEmailOtpLoginAttemptPublished}
          errorText={state?.error?.text}
          context={state}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
        />
      )}
      {(isLoginOtpVerification || isSignupOtpVerification) && (
        <OTPVerificationScreen
          authFlow={isSignupOtpVerification ? 'signup' : 'login'}
          variant={isLoginOtpVerification ? 'banner' : undefined}
          bannerImageDesktop={
            isLoginOtpVerification ? bannerLoginImage : undefined
          }
          hideLogo={isLoginOtpVerification ? false : undefined}
          consumeInitialOtpChallengeRequestTrigger={
            consumeInitialOtpChallengeRequestTrigger
          }
          onFormUpdate={updateForm}
          onOTPVerificationClick={async (code: string) => {
            if (state.next) {
              await state.next('oneTimeCodeId', { ...form, code });
            }
          }}
          onClickResend={async () => {
            if (state.next) {
              await state.next('resend', {});
            }
          }}
          onLoginOtpRouteKnown={
            isLoginOtpVerification ? publishEmailOtpLoginAttemptOnce : undefined
          }
          onBackButtonClick={async () => {
            if (state.next) {
              const isLoginOTP = isLoginOtpVerificationScreen(state.screenName);
              await state.next(isLoginOTP ? 'sd-XX997C-' : '_3P5G3HnMB', form);
            }
          }}
          errorText={state?.error?.text}
          context={state}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
        />
      )}
      {routePhoneSmsOtpAuthScreen({
        screenName: state?.screenName,
        state,
        formWithoutSmsAuthFlow,
        smsAuthForm,
        smsScreenContext,
        smsAuthAllowedCountries: [...smsAuthAllowedCountries],
        onFormUpdate: setForm,
        onPhoneUpdate: setSmsPhone,
        onPhoneLoginBack: resetMobileSmsLoginFlow,
        resetError: resetDescopeError,
        consumeInitialOtpChallengeRequestTrigger,
        PhoneLoginScreen,
        OtpVerificationScreen: OTPVerificationScreen,
      })}
      {routeMfaChallengeScreen({
        screenName: state?.screenName,
        state,
        form: formWithoutSmsAuthFlow,
        onFormUpdate: setForm,
        resetError: resetDescopeError,
        consumeInitialOtpChallengeRequestTrigger:
          consumeInitialOtpChallengeRequestTrigger,
        OtpVerificationScreen: OTPVerificationScreen,
      })}
      {state?.screenName === ScreenNames.CHOOSE_USERNAME_EMAIL_OTP && (
        <UsernameScreen
          onClick={async () => {
            if (state.next) {
              await state.next('RgRKIeo9Mb', form);
            }
          }}
          onChange={(data: DescopeFormData) =>
            setForm(prev => ({ ...prev, ...data }))
          }
          onBackButtonClick={async () => {
            if (state.next) {
              await state.next('-YYOF7o-bI', form);
            }
          }}
          onValidateUsername={async () => {
            if (state.next) {
              await state.next('Tyk61-L5EC', form);
            }
          }}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
          errorText={state?.error?.text}
          context={state}
          prefilledUsername={prefilledUsername}
        />
      )}
      {state?.screenName === ScreenNames.SOCIAL_SIGNUP_USERNAME && (
        <UsernameScreen
          onClick={async () => {
            if (state.next) {
              await state.next('e4kL6KNRMP', form);
            }
          }}
          onChange={(data: DescopeFormData) =>
            setForm(prev => ({ ...prev, ...data }))
          }
          onValidateUsername={async () => {
            if (state.next) {
              await state.next('pZG7UDjKAH', form);
            }
          }}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
          errorText={state?.error?.text}
          isSocialUsername={true}
          context={state}
          prefilledUsername={prefilledUsername}
        />
      )}
    </Descope>
  );
};

const LocalizedDescopeApp: React.FC = () => {
  useLocalizationModeSync();
  const [initialRoute] = useState(() => ({
    flowId: getInitialFlowId(),
    resetPasswordToken: getResetPasswordTokenFromRoute(),
  }));
  const { flowId, resetPasswordToken } = initialRoute;

  return (
    <div className="auth-tw">
      {flowId === UNIVERSAL_LOGIN_FLOW_ID ? (
        <UniversalLoginFlow />
      ) : flowId === ADD_MFA_WITH_SMS_OTP_FLOW_ID ? (
        <AddMfaWithSmsOtpFlow />
      ) : flowId === ADD_MFA_WITH_TOTP_FLOW_ID ? (
        <AddMfaWithTotpFlow />
      ) : flowId === RESET_SMS_MFA_FLOW_ID ? (
        <ResetSmsMfaFlow />
      ) : flowId === RESET_TOTP_MFA_FLOW_ID ? (
        <ResetTotpMfaFlow />
      ) : flowId === MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID ? (
        <MobileConsumerPromotionSetEmailFlow />
      ) : flowId === FORGOT_USERNAME_FLOW_ID ? (
        <ForgotUsernameFlow />
      ) : flowId === REQUEST_PASSWORD_RESET_FLOW_ID ? (
        <RequestPasswordResetFlow />
      ) : flowId === RESET_PASSWORD_FLOW_ID ? (
        <ResetPasswordFlow confirmationToken={resetPasswordToken} />
      ) : (
        <HostedDescopeFlow flowId={flowId} />
      )}
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    document.documentElement.classList.add('auth-tw');

    // Check if this is a page refresh and redirect to appropriate URL
    const navigationEntries = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    const isPageRefresh =
      navigationEntries.length > 0 && navigationEntries[0]?.type === 'reload';

    if (
      isPageRefresh &&
      !isForgotUsernameRoute() &&
      !isRequestPasswordResetRoute() &&
      !isResetPasswordRoute()
    ) {
      window.location.href = `${LINKTREE_BASE_URL}/login`;
    }

    return () => {
      document.documentElement.classList.remove('auth-tw');
    };
  }, []);

  return (
    <AuthProvider
      projectId={DESCOPE_PROJECT_ID}
      baseUrl={DESCOPE_BASE_URL}
      baseStaticUrl={DESCOPE_BASE_URL + '/pages'}
    >
      <StatsigDualInstanceProvider
        sdkKey={STATSIG_SDK_KEY}
        stage={STATSIG_STAGE}
        user={{}}
        userWarehouseNative={{
          locale: getCompoundLocale(detectStatsigLocale()),
          customIDs: {
            // In mobile WebView sessions, evaluate gates under the native
            // device stableID (forwarded as the mobile_stable_id hash param)
            // so stableID-targeted overrides match the same identity as the
            // native app. Falls back to the browser id on the web.
            stableID: getMobileStableId() ?? getBrowserId(),
          },
        }}
        service="auth0"
        waitForInitialization={true}
      >
        <LocalizedDescopeApp />
      </StatsigDualInstanceProvider>
    </AuthProvider>
  );
};

export default App;
